// Display labels only: action names and permission slugs remain stable for access checks.
const permissionLabels = {
  view: 'View',
  create: 'Create',
  edit: 'Edit',
  delete: 'Delete',
  status: 'Change Status',
  send_approval: 'Request Approval',
  resend_approval: 'Resubmit for Approval',
  cancel_approval: 'Withdraw Request',
  approve: 'Approve',
  reject: 'Reject',
  manage: 'Manage',
  view_analytics: 'View Analytics',
  publish: 'Publish',
  unpublish: 'Unpublish',
  account_recover: 'Restore Account',
};

// Singular labels describe an operation; plural labels describe a list or area.
const resources = {
  dashboard_management: ['Dashboard', 'Dashboard'],
  profile_management: ['Profile', 'Profile'],
  course_management: ['Course', 'Courses'],
  classroom_management: ['Classroom', 'Classrooms'],
  course_approval_workflow: ['Course', 'Course Approval Requests'],
  certificate_management: ['Certificate', 'Certificates'],
  trainee_management: ['Trainee', 'Trainees'],
  roles_and_access_management: ['Role and Access', 'Roles and Access'],
  question_bank_management: ['Question', 'Question Bank'],
  announcement_management: ['Announcement', 'Announcements'],
  notification_management: ['Notification', 'Notifications'],
  setting_management: ['Settings', 'Settings'],
  course_module_management: ['Course Module', 'Course Modules'],
  course_module_content_management: ['Module Content', 'Module Content'],
  classroom_assignment: ['Classroom Assignment', 'Classroom Assignments'],
  classroom_assessment: ['Classroom Assessment', 'Classroom Assessments'],
  classroom_resources: ['Classroom Resource', 'Classroom Resources'],
  classroom_liveclasses: ['Live Class', 'Live Classes'],
  classroom_community: ['Classroom Community', 'Classroom Community'],
  classroom_trainees: ['Classroom Trainee', 'Classroom Trainees'],
  classroom_assignment_resource: ['Assignment Resource', 'Assignment Resources'],
  classroom_assignment_response: ['Assignment Response', 'Assignment Responses'],
  classroom_assessment_question: ['Assessment Question', 'Assessment Questions'],
  classroom_assessment_result: ['Assessment Result', 'Assessment Results'],
  roles_and_access_user: ['User', 'Users'],
  roles_and_access_permissions: ['Role Permissions', 'Role Permissions'],
  settings_about_organization: ['Organization Details', 'Organization Details'],
  settings_faq: ['FAQ', 'FAQs'],
  settings_query: ['Support Query', 'Support Queries'],
  settings_payments: ['Payment Settings', 'Payment Settings'],
  settings_account_deletion: ['Deleted Account', 'Deleted Accounts'],
};
const titleCase = value => value.replace(/[_-]+/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase()).replace(/\bFaqs?\b/gi, word => word.toLowerCase() === 'faqs' ? 'FAQs' : 'FAQ');
const cleanSubject = value => String(value || '')
  .replace(/[_-]+/g, ' ')
  .replace(/\bmanagement\b/gi, '')
  .replace(/^(?:(?:the|a|an|new|existing|list of)\s+)+/i, '')
  .replace(/\s+(?:in|from|within|on)\s+(?:the\s+)?(?:sidebar|system|platform)\b.*$/i, '')
  .replace(/\s+/g, ' ').trim();
const moduleKey = module => String(module?.slug || module?.name || '').toLowerCase().replace(/[\s-]+/g, '_');

export function getPermissionLabel(action, module) {
  const key = String(action.action_name || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  const verb = permissionLabels[key] || titleCase(key);
  const description = String(action.description || '').trim();
  const context = cleanSubject(module?.name || module?.slug);
  const evidence = `${description} ${action.name || ''} ${action.slug || ''}`;
  const slug = moduleKey(module);
  const resource = resources[slug];
  const singular = resource?.[0] || titleCase(context);
  const plural = resource?.[1] || singular;

  if (key === 'view' && /dashboard/i.test(`${context} ${evidence}`)) {
    if (/\bcards?\b/i.test(evidence)) return 'View Dashboard Cards';
    if (/\b(graphs?|charts?)\b/i.test(evidence)) return 'View Dashboard Charts';
    if (/latest[ _-]*updates?/i.test(evidence)) return 'View Latest Updates';
    return 'View Dashboard';
  }
  if (key === 'account_recover') return 'Restore Deleted Accounts';
  if (key === 'delete' && /permanently delete/i.test(description)) return 'Permanently Delete Accounts';
  if (key === 'view' && /deleted accounts/i.test(description)) return 'View Deleted Accounts';
  if (['send_approval', 'resend_approval', 'cancel_approval'].includes(key)) {
    const target = /course/i.test(`${context} ${evidence}`) ? 'Course' : singular;
    const verbs = { send_approval: 'Request', resend_approval: 'Resubmit', cancel_approval: 'Withdraw' };
    return `${verbs[key]} ${target ? target + ' ' : ''}Approval`;
  }
  if (key === 'view_analytics') return `View ${singular ? singular + ' ' : ''}Analytics`;
  if (key === 'status') return `Change ${singular ? singular + ' ' : ''}Status`;

  // Preserve specific targets such as comments or ratings within a broader area.
  const actionPhrase = key.replace(/_/g, ' ');
  const described = description.replace(/^permission to\s+/i, '');
  let subject = key && described.toLowerCase().startsWith(actionPhrase + ' ')
    ? cleanSubject(described.slice(actionPhrase.length)) : '';
  const generic = !subject || [context, singular, plural].some(value => value && value.toLowerCase() === subject.toLowerCase());
  if (resource && generic) subject = ['view', 'manage'].includes(key) ? plural : singular;
  if (!subject) {
    const raw = String(action.name || action.slug || '').replace(/[_-]+/g, ' ');
    subject = cleanSubject(raw.replace(new RegExp('(?:^|\\s)' + actionPhrase + '$', 'i'), '')) || context;
  }
  if (subject.toLowerCase() === actionPhrase) subject = context;
  return [verb, titleCase(subject)].filter(Boolean).join(' ') || 'Permission';
}
